import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    Trophy, Star, TrendingUp, TrendingDown, Zap, Shield, Brain, RefreshCw,
    Loader2, ChevronDown, ChevronUp, ImageIcon, Download, Users, Target
} from 'lucide-react';

const POSITION_COLORS = {
    1: 'from-yellow-500/30 to-amber-500/10 border-yellow-500/40',
    2: 'from-slate-300/20 to-gray-500/10 border-slate-400/30',
    3: 'from-orange-600/20 to-amber-700/10 border-orange-500/30',
};

function TeamPreviewCard({ team, expanded, onToggle, onGenerateImage, generatingImage }) {
    const pos = team.predicted_position;
    const isTop3 = pos <= 3;
    const borderClass = isTop3 ? POSITION_COLORS[pos] : 'from-white/5 to-transparent border-white/5';

    return (
        <div className={`glass-card border bg-gradient-to-r ${borderClass} overflow-hidden`}
            data-testid={`team-preview-${team.id}`}>
            <div className="p-5 cursor-pointer" onClick={onToggle}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-10 h-10 rounded-xl font-bold text-lg"
                            style={{ backgroundColor: `${team.color}25`, color: team.color }}>
                            #{pos}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-base font-bold text-white">{team.name}</span>
                                {team.titles > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 tech-mono">
                                        {team.titles}x
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-white/30 mt-0.5">
                                Captain: {team.captain} | Coach: {team.coach}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right hidden md:block">
                            <div className="text-xs text-white/30">Strength</div>
                            <div className="text-lg font-bold" style={{ color: team.color }}>{team.strength_score}</div>
                        </div>
                        <div className="text-right hidden md:block">
                            <div className="text-xs text-white/30">Sim Win%</div>
                            <div className="text-sm font-bold text-cyan-400">{team.tournament_win_rate || '—'}%</div>
                        </div>
                        {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                    </div>
                </div>

                {/* Strength Bars */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                    <div>
                        <div className="flex items-center justify-between text-[10px] mb-1">
                            <span className="text-white/30 tech-mono">BATTING</span>
                            <span className="text-white/50">{team.batting_strength}</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all"
                                style={{ width: `${team.batting_strength}%` }} />
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center justify-between text-[10px] mb-1">
                            <span className="text-white/30 tech-mono">BOWLING</span>
                            <span className="text-white/50">{team.bowling_strength}</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all"
                                style={{ width: `${team.bowling_strength}%` }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Details */}
            {expanded && (
                <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
                    {/* Key Players */}
                    <div>
                        <div className="text-[10px] text-white/30 tech-mono mb-2">TOP PLAYERS</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {team.top_3_players?.map((p, i) => (
                                <div key={i} className="bg-white/3 rounded-lg p-3 flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold"
                                        style={{ backgroundColor: `${team.color}20`, color: team.color }}>
                                        {i + 1}
                                    </div>
                                    <div>
                                        <div className="text-xs text-white font-medium">{p.name}</div>
                                        <div className="text-[10px] text-white/30">{p.role} | Impact: {p.impact_score}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* GPT Analysis */}
                    {team.gpt_verdict && (
                        <div className="space-y-2">
                            <div className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg p-4 border border-purple-500/20">
                                <div className="text-[10px] text-white/30 tech-mono mb-1">AI VERDICT</div>
                                <div className="text-sm text-white/80 font-medium">{team.gpt_verdict}</div>
                            </div>
                            {team.gpt_strengths && (
                                <div className="bg-green-500/5 rounded-lg p-3 border border-green-500/10">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <TrendingUp className="w-3 h-3 text-green-400" />
                                        <span className="text-[10px] text-green-400 tech-mono">STRENGTHS</span>
                                    </div>
                                    <div className="text-xs text-white/60">{team.gpt_strengths}</div>
                                </div>
                            )}
                            {team.gpt_weaknesses && (
                                <div className="bg-red-500/5 rounded-lg p-3 border border-red-500/10">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <TrendingDown className="w-3 h-3 text-red-400" />
                                        <span className="text-[10px] text-red-400 tech-mono">WEAKNESSES</span>
                                    </div>
                                    <div className="text-xs text-white/60">{team.gpt_weaknesses}</div>
                                </div>
                            )}
                            {team.gpt_x_factor && (
                                <div className="bg-amber-500/5 rounded-lg p-3 border border-amber-500/10">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <Zap className="w-3 h-3 text-amber-400" />
                                        <span className="text-[10px] text-amber-400 tech-mono">X-FACTOR</span>
                                    </div>
                                    <div className="text-xs text-white/60">{team.gpt_x_factor}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Stats Grid */}
                    <div className="grid grid-cols-4 gap-2">
                        <div className="bg-white/3 rounded-lg p-2 text-center">
                            <div className="text-[10px] text-white/20 tech-mono">SQUAD</div>
                            <div className="text-sm font-bold text-white">{team.squad_size}</div>
                        </div>
                        <div className="bg-white/3 rounded-lg p-2 text-center">
                            <div className="text-[10px] text-white/20 tech-mono">BATTERS</div>
                            <div className="text-sm font-bold text-green-400">{team.batters_count}</div>
                        </div>
                        <div className="bg-white/3 rounded-lg p-2 text-center">
                            <div className="text-[10px] text-white/20 tech-mono">BOWLERS</div>
                            <div className="text-sm font-bold text-blue-400">{team.bowlers_count}</div>
                        </div>
                        <div className="bg-white/3 rounded-lg p-2 text-center">
                            <div className="text-[10px] text-white/20 tech-mono">TITLE PROB</div>
                            <div className="text-sm font-bold text-yellow-400">{team.gpt_title_probability || '—'}</div>
                        </div>
                    </div>

                    {/* Generate Image Button */}
                    <div className="flex justify-end">
                        <Button onClick={() => onGenerateImage(team)}
                            disabled={generatingImage}
                            variant="outline"
                            className="border-white/10 text-white hover:bg-white/5 text-xs"
                            data-testid={`gen-image-${team.id}`}>
                            {generatingImage ? (
                                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</>
                            ) : (
                                <><ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Generate Shareable Card</>
                            )}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function SeasonPreview() {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [expandedTeam, setExpandedTeam] = useState(null);
    const [generatingImage, setGeneratingImage] = useState(null);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [retrainingLog, setRetrainingLog] = useState(null);

    const fetchPreview = useCallback(async () => {
        try {
            const [prevRes, logRes] = await Promise.all([
                API.get('/season-preview'),
                API.get('/season-preview/retraining-log'),
            ]);
            setPreview(prevRes.data);
            setRetrainingLog(logRes.data);
        } catch (err) {
            console.error('Failed to load season preview', err);
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchPreview(); }, [fetchPreview]);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await API.post('/season-preview/generate');
            setPreview(res.data);
            toast.success('Season Preview generated with GPT-5.2 analysis');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to generate preview');
        }
        setGenerating(false);
    };

    const handleGenerateImage = async (team) => {
        setGeneratingImage(team.id);
        setGeneratedImage(null);
        try {
            const res = await API.post('/season-preview/generate-image', {
                team: team.short_name,
                position: team.predicted_position,
                key_player: team.key_player?.name || 'Unknown',
                strength_score: team.strength_score,
            });
            if (res.data.success) {
                setGeneratedImage({ team: team.short_name, base64: res.data.image_base64 });
                toast.success(`Season card generated for ${team.short_name}`);
            } else {
                toast.error(res.data.error || 'Image generation failed');
            }
        } catch (err) {
            toast.error('Image generation failed');
        }
        setGeneratingImage(null);
    };

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="glass-card p-12 text-center">
                    <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
            </div>
        );
    }

    const teams = preview?.teams || [];
    const overall = preview?.gpt_analysis?.overall || {};

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5" data-testid="season-preview">
            {/* Header */}
            <div className="glass-card p-5 border-l-4 border-l-yellow-500">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                        <div>
                            <h1 className="heading-primary text-base md:text-xl text-white">IPL 2026 SEASON PREVIEW</h1>
                            <div className="text-xs text-white/30 mt-0.5">
                                AI-powered team analysis, predicted standings, and shareable graphics
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={handleGenerate} disabled={generating}
                            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white text-xs btn-glow"
                            data-testid="generate-preview-btn">
                            {generating ? (
                                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</>
                            ) : (
                                <><Brain className="w-3.5 h-3.5 mr-1.5" /> Generate GPT-5.2 Analysis</>
                            )}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={fetchPreview} className="text-white/30 hover:text-white">
                            <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* GPT Overall Analysis */}
            {preview?.generated && overall && Object.keys(overall).length > 0 && (
                <div className="glass-card p-5 border border-purple-500/20 bg-gradient-to-r from-purple-500/5 to-cyan-500/5">
                    <div className="flex items-center gap-2 mb-4">
                        <Brain className="w-4 h-4 text-purple-400" />
                        <h2 className="heading-primary text-sm text-white">GPT-5.2 SEASON INTELLIGENCE</h2>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 tech-mono">AI GENERATED</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {overall.title_favorites && (
                            <div className="bg-white/3 rounded-lg p-4">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <Trophy className="w-3.5 h-3.5 text-yellow-400" />
                                    <span className="text-[10px] text-yellow-400 tech-mono">TITLE FAVORITES</span>
                                </div>
                                <div className="text-sm text-white/70">{overall.title_favorites}</div>
                            </div>
                        )}
                        {overall.dark_horse && (
                            <div className="bg-white/3 rounded-lg p-4">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <Zap className="w-3.5 h-3.5 text-amber-400" />
                                    <span className="text-[10px] text-amber-400 tech-mono">DARK HORSE</span>
                                </div>
                                <div className="text-sm text-white/70">{overall.dark_horse}</div>
                            </div>
                        )}
                        {overall.season_narrative && (
                            <div className="bg-white/3 rounded-lg p-4 md:col-span-2">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <Star className="w-3.5 h-3.5 text-cyan-400" />
                                    <span className="text-[10px] text-cyan-400 tech-mono">SEASON NARRATIVE</span>
                                </div>
                                <div className="text-sm text-white/70">{overall.season_narrative}</div>
                            </div>
                        )}
                        {overall.bold_prediction && (
                            <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-lg p-4 border border-red-500/20 md:col-span-2">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <Target className="w-3.5 h-3.5 text-red-400" />
                                    <span className="text-[10px] text-red-400 tech-mono">BOLD PREDICTION</span>
                                </div>
                                <div className="text-sm text-white/80 font-medium">{overall.bold_prediction}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Team Rankings */}
            <div className="space-y-3">
                {teams.map(team => (
                    <TeamPreviewCard
                        key={team.id}
                        team={team}
                        expanded={expandedTeam === team.id}
                        onToggle={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
                        onGenerateImage={handleGenerateImage}
                        generatingImage={generatingImage === team.id}
                    />
                ))}
            </div>

            {/* Generated Image Preview */}
            {generatedImage && (
                <div className="glass-card p-5 border border-cyan-500/20" data-testid="generated-preview-image">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <ImageIcon className="w-4 h-4 text-cyan-400" />
                            <span className="heading-primary text-sm text-white">GENERATED CARD — {generatedImage.team}</span>
                        </div>
                        <a href={`data:image/png;base64,${generatedImage.base64}`}
                            download={`IPL2026_${generatedImage.team}_Preview.png`}
                            className="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300">
                            <Download className="w-3.5 h-3.5" /> Download
                        </a>
                    </div>
                    <img src={`data:image/png;base64,${generatedImage.base64}`}
                        alt={`${generatedImage.team} Preview`}
                        className="w-full max-w-lg mx-auto rounded-lg border border-white/10" />
                </div>
            )}

            {/* Model Retraining Log */}
            <div className="glass-card p-5 border border-white/5">
                <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-4 h-4 text-cyan-400" />
                    <h2 className="heading-primary text-sm text-white">MODEL RETRAINING LOG</h2>
                    <span className="text-[10px] text-white/20 tech-mono">
                        {retrainingLog?.total_retrained || 0} events | {retrainingLog?.total_analyzed || 0} analyzed
                    </span>
                </div>
                {retrainingLog?.retraining_events?.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {retrainingLog.retraining_events.slice(0, 10).map((e, i) => (
                            <div key={i} className="flex items-center justify-between bg-white/3 rounded-lg p-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                                        <Brain className="w-4 h-4 text-cyan-400" />
                                    </div>
                                    <div>
                                        <div className="text-xs text-white/70">Match: {e.match_id?.slice(0, 20)}</div>
                                        <div className="text-[10px] text-white/30">
                                            {e.correct}/{e.total_predictions} correct
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-cyan-400">{e.accuracy_after}%</div>
                                    <div className="text-[10px] text-white/20">{new Date(e.timestamp).toLocaleDateString()}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-6">
                        <Users className="w-8 h-8 text-white/10 mx-auto mb-2" />
                        <div className="text-xs text-white/30">No retraining events yet</div>
                        <div className="text-[10px] text-white/15 mt-1">
                            Model automatically retrains after each completed IPL 2026 match
                        </div>
                    </div>
                )}
            </div>

            {/* Info Footer */}
            <div className="glass-card p-4 border border-white/5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px] text-white/15">
                    <div className="flex items-center gap-1.5">
                        <Star className="w-3 h-3 text-yellow-400/30" />
                        Rankings based on Monte Carlo tournament simulation
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Brain className="w-3 h-3 text-purple-400/30" />
                        GPT-5.2 analysis uses real squad and historical data
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Shield className="w-3 h-3 text-cyan-400/30" />
                        Model auto-retrains after each match completion
                    </div>
                </div>
            </div>
        </div>
    );
}
